import { useEffect, useRef } from 'react';

interface AppModalProps {
  open: boolean;
  title?: string;
  message: string;
  type?: 'info' | 'warning' | 'error' | 'success';
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm: () => void;
  onCancel?: () => void;
}

const icons = {
  info: '💬',
  warning: '⚠️',
  error: '✖',
  success: '✓',
};

const colors = {
  info: 'bg-[#1e8585]',
  warning: 'bg-amber-500',
  error: 'bg-red-600',
  success: 'bg-emerald-600',
};

export const AppModal = ({
  open, title, message, type = 'info',
  confirmLabel = 'OK', cancelLabel,
  onConfirm, onCancel,
}: AppModalProps) => {
  const btnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (open) setTimeout(() => btnRef.current?.focus(), 50);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && onCancel) onCancel();
      if (e.key === 'Enter') onConfirm();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onConfirm, onCancel]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-[1px]">
      <div className="bg-white rounded shadow-2xl border border-gray-200 w-full max-w-sm overflow-hidden animate-in fade-in zoom-in-95 duration-150">
        {/* Header */}
        <div className={`${colors[type]} px-4 py-3 flex items-center gap-2`}>
          <span className="text-white text-base">{icons[type]}</span>
          <span className="text-white font-bold text-[13px] tracking-wide">
            {title || 'Aarthika Pro'}
          </span>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          <p className="text-gray-700 text-[13px] leading-relaxed">{message}</p>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-2 px-6 pb-5">
          {cancelLabel && onCancel && (
            <button
              onClick={onCancel}
              className="px-5 py-1.5 text-[13px] font-semibold bg-gray-100 hover:bg-gray-200 text-gray-700 rounded transition-colors"
            >
              {cancelLabel}
            </button>
          )}
          <button
            ref={btnRef}
            onClick={onConfirm}
            className={`px-6 py-1.5 text-[13px] font-bold text-white rounded transition-colors ${colors[type]} hover:opacity-90`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};
